// AppShell — DRY: the shared sidebar shell for the admin, student and tutor
// layouts. From `md` (768px) up it is the original fixed 224px sidebar next to
// <main>. Below `md` the nav becomes an off-canvas drawer that opens under a
// fixed top bar. Every breakpoint-dependent class lives in the constants below
// so the desktop classes can be checked against the old layouts at a glance.
import { NavLink, Outlet } from "react-router-dom";
import SignOutButton from "../auth/SignOutButton.js";
import { useCloseOnEscape, useCloseOnRouteChange, useDrawerState } from "../../hooks/useDrawerState.js";

export interface NavItem {
  to:    string;
  label: string;
  end:   boolean;
}

const NAV_ID = "app-nav";

// Desktop (md and up): exactly the classes the three layouts used before.
const NAV_DESKTOP = "w-56 shrink-0 bg-dm-navy text-white p-4 space-y-1";
// Below md: fixed under the top bar (h-14), scrollable, slides in from the left.
const NAV_DRAWER =
  "max-md:fixed max-md:top-14 max-md:bottom-0 max-md:left-0 max-md:z-40 max-md:overflow-y-auto " +
  "max-md:transition-[transform,visibility] max-md:duration-200 motion-reduce:transition-none";
const NAV_CLOSED = "max-md:-translate-x-full max-md:invisible";
const NAV_OPEN   = "max-md:translate-x-0";

const TOP_BAR  = "fixed inset-x-0 top-0 z-20 flex h-14 items-center gap-3 bg-dm-navy px-4 text-white md:hidden";
const BACKDROP = "fixed inset-x-0 bottom-0 top-14 z-30 bg-black/40 md:hidden";
const MAIN_OFFSET = "max-md:pt-14";

const LINK_BASE   = "flex items-center justify-between rounded-md px-3 py-2 text-sm";
const LINK_ACTIVE = "bg-dm-blue text-white";
const LINK_IDLE   = "text-white/80 hover:bg-dm-blue/50";
// Padding, not margin: the nav's space-y-1 overrides a child's margin-top.
const SIGN_OUT_WRAP = "flex flex-col border-t border-white/10 pt-3";

interface MobileTopBarProps {
  title:    string;
  isOpen:   boolean;
  onToggle: () => void;
}

function MobileTopBar({ title, isOpen, onToggle }: MobileTopBarProps) {
  return (
    <header className={TOP_BAR}>
      <button
        type="button"
        onClick={onToggle}
        aria-label={isOpen ? "Close navigation" : "Open navigation"}
        aria-expanded={isOpen}
        aria-controls={NAV_ID}
        className="-ml-2 rounded-md p-2 hover:bg-dm-blue/50 focus:outline-none focus:ring-2 focus:ring-white/60"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          {isOpen ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
        </svg>
      </button>
      <span className="text-sm font-semibold tracking-wide">{title}</span>
    </header>
  );
}

function DrawerBackdrop({ onClose }: { onClose: () => void }) {
  return <div className={BACKDROP} onClick={onClose} aria-hidden="true" />;
}

interface NavLinkListProps {
  items:      readonly NavItem[];
  badgeFor?:  (label: string) => number | null;
  onNavigate: () => void;
}

function NavLinkList({ items, badgeFor, onNavigate }: NavLinkListProps) {
  return (
    <>
      {items.map((item) => {
        const badgeCount = badgeFor?.(item.label) ?? null;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            className={({ isActive }) => `${LINK_BASE} ${isActive ? LINK_ACTIVE : LINK_IDLE}`}
          >
            <span>{item.label}</span>
            {!!badgeCount && (
              <span className="rounded-full bg-amber-400 px-1.5 py-0.5 text-[10px] font-semibold text-dm-navy">
                {badgeCount}
              </span>
            )}
          </NavLink>
        );
      })}
    </>
  );
}

interface AppShellProps {
  title:     string;
  items:     readonly NavItem[];
  badgeFor?: (label: string) => number | null;
}

export default function AppShell({ title, items, badgeFor }: AppShellProps) {
  const { isOpen, close, toggle } = useDrawerState();
  useCloseOnEscape(isOpen, close);
  useCloseOnRouteChange(close);

  return (
    <div className="dm-layout">
      <MobileTopBar title={title} isOpen={isOpen} onToggle={toggle} />
      {isOpen && <DrawerBackdrop onClose={close} />}
      <nav
        id={NAV_ID}
        aria-label={`${title} navigation`}
        className={`${NAV_DESKTOP} ${NAV_DRAWER} ${isOpen ? NAV_OPEN : NAV_CLOSED}`}
      >
        <p className="text-xs uppercase tracking-wide text-white/50 px-2 mb-3">{title}</p>
        <NavLinkList items={items} onNavigate={close} {...(badgeFor ? { badgeFor } : {})} />
        <div className={SIGN_OUT_WRAP}><SignOutButton /></div>
      </nav>
      <main className={`dm-main ${MAIN_OFFSET}`}><Outlet /></main>
    </div>
  );
}
